import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { environment } from '../../environments/environment';
import { SearchEditionParams } from '../models/search-edition-params';
import { SearchEditionResults, SearchEditionResultRow } from '../models/search-edition-results';
import { SearchEditionCache } from '../models/search-edition-cache';
import { SectionKey } from '../models/search-edition-params';

/* Http parameters type for critical edition text search */
export type SearchHttpParamsTypes = 'search';

@Injectable()
export class SearchEditionService {
    private httpHeaders: HttpHeaders;
    private httpParams: Map<SearchHttpParamsTypes, HttpParams>;
    private searchParams$: BehaviorSubject<SearchEditionParams>;
    private searchResults$: BehaviorSubject<SearchEditionResults>;
    private searchAllParams$: BehaviorSubject<SearchEditionParams>;
    private searchAllResults$: BehaviorSubject<SearchEditionResults>;
    private searching$: BehaviorSubject<boolean>;
    private resultsCache$: BehaviorSubject<SearchEditionCache>;
    private selectedResult$: BehaviorSubject<SearchEditionResultRow|null>;
    private searchAll$: BehaviorSubject<boolean>;

    constructor(private http: HttpClient) {
        this.init();
    }

    init(): void {
        /* Common headers */
        this.httpHeaders = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': environment.apiKey,
            'X-PhiBor-App': environment.phiborApp
        });

        this.httpParams = new Map<SearchHttpParamsTypes, HttpParams>();

        /* Observables initialization */
        this.searchParams$ = new BehaviorSubject<SearchEditionParams>(new SearchEditionParams());
        this.searchResults$ = new BehaviorSubject<SearchEditionResults>([]);
        this.searchAllParams$ = new BehaviorSubject<SearchEditionParams>(new SearchEditionParams());
        this.searchAllResults$ = new BehaviorSubject<SearchEditionResults>([]);
        this.searching$ = new BehaviorSubject<boolean>(false);
        this.resultsCache$ = new BehaviorSubject<SearchEditionCache>([]);
        this.selectedResult$ = new BehaviorSubject<SearchEditionResultRow>(null);
        this.searchAll$ = new BehaviorSubject<boolean>(false);
    }

    /* Critical edition search endpoint */
    private getEndpoint(isSample: boolean): string {
        return environment.endpoints.admin
            + '/ced/'
            + (isSample ? environment.criticalEdition.type.sample : environment.criticalEdition.type.public)
            + '/search';
    }

    /* Critical edition search API call */
    private search(searchParams: SearchEditionParams): void {
        this.http.post<SearchEditionResults>(
            this.getEndpoint(false) + '/' + searchParams.section,
            searchParams,
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('search')
            }
        ).subscribe(
            response => {
                if (response) {
                    this.searching$.next(false);
                    this.searchResults$.next(response);
                }
            },
            error => {
                console.log(error);
                this.searching$.next(false);
                this.searchResults$.next([]);
            }
        );
    }

    /* Critical edition search all API call */
    private searchAll(searchParams: SearchEditionParams): void {
        this.http.post<SearchEditionResults>(
            this.getEndpoint(false) + '/all',
            searchParams,
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('search')
            }
        ).subscribe(
            response => {
                if (response) {
                    this.searching$.next(false);
                    this.searchAllResults$.next(response);
                    this.searchAll$.next(true);
                }
            },
            error => {
                console.log(error);
                this.searching$.next(false);
                this.searchAllResults$.next([]);
            }
        );
    }

    updateSearchParams(searchParams: SearchEditionParams): void {
        this.searchParams$.next(searchParams);
    }

    updateSearchAllParams(searchParams: SearchEditionParams): void {
        this.searchAllParams$.next(searchParams);
    }

    /* Run textual search */
    updateSearchResults(searchParams: SearchEditionParams): void {
        this.updateSearchParams(searchParams);
        this.searching$.next(true);
        this.search(searchParams);
    }

    /* Run textual search in all sections of the critical edition */
    updateSearchAllResults(searchParams: SearchEditionParams): void {
        this.updateSearchAllParams(searchParams);
        this.searching$.next(true);
        this.searchAll$.next(false);
        this.searchAll(searchParams);
    }

    /* Get search edition parameters */
    getSearchParams(): Observable<SearchEditionParams> {
        return this.searchParams$.asObservable();
    }

    /* Get the search in all sections edition parameters */
    getSearchAllParams(): Observable<SearchEditionParams> {
        return this.searchAllParams$.asObservable();
    }

    /* Get search edition results */
    getSearchResults(): Observable<SearchEditionResults> {
        return this.searchResults$.asObservable();
    }

    /* Reset search edition results */
    resetSearchResults(): void {
        this.searchResults$.next([]);
    }

    /* Get search edition results (all sections) */
    getSearchAllResults(): Observable<SearchEditionResults> {
        return this.searchAllResults$.asObservable();
    }

    /* Reset search edition results (all sections) */
    resetSearchAllResults(): void {
        this.searchAllResults$.next([]);
        this.searchAll$.next(false);
    }

    /* Get searching status */
    isSearchInProgress(): Observable<boolean> {
        return this.searching$.asObservable();
    }

    /* Get search on all sections status */
    isSearchAll(): Observable<boolean> {
        return this.searchAll$.asObservable();
    }

    cachePageResults(section: SectionKey, page: number, results: SearchEditionResults): void {
        const resultsCache = this.resultsCache$.getValue();
        let pageFound = false;

        /* Overwrites the results if the page already exists */
        for (const cacheElement of resultsCache) {
            if (cacheElement.section === section && cacheElement.page === page) {
                cacheElement.results = results;
                pageFound = true;
            }
        }

        if (!pageFound) {
            resultsCache.push({section: section, page: page, results: results});
        }
        this.resultsCache$.next(resultsCache);
    }

    getCachedSearchResultsByPage(section: SectionKey, page: number): SearchEditionResults|void {
        const resultsCache = this.resultsCache$.getValue();

        for (const cacheElement of resultsCache) {
            if (cacheElement.section === section && cacheElement.page === page) {
                return cacheElement.results;
            }
        }
    }

    getSearchResultsCache(): Observable<SearchEditionCache> {
        return this.resultsCache$.asObservable();
    }

    resetSearchResultsCache(): void {
        this.resultsCache$.next([]);
    }

    setSearchEditionSelected(resultRow: SearchEditionResultRow): void {
        this.selectedResult$.next(resultRow);
    }

    getSearchEditionSelected(): Observable<SearchEditionResultRow> {
        return this.selectedResult$.asObservable();
    }

    resetSearchEditionSelected(): void {
        this.selectedResult$.next(null);
    }

}



// WEBPACK FOOTER //
// ./src/app/services/search-edition.service.ts